import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../../context/AuthContext';
import LineChart from 'components/charts/LineChart';

const Linkbuilding = ({ site, siteId, dateFrom, dateTo, currentLang, onStats }) => {
  const { token, API_URL } = useAuth();
  const [linksPeriod, setLinksPeriod] = useState([]); // только за период
  const [linksBefore, setLinksBefore] = useState(0); // до периода
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState({ series: [], options: {} });

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('ru-RU');
  };

  // Дата последней проверки
  const getLastCheckDate = (dend) => {
    if (!dend) return '-';
    const checkDate = new Date(dend);
    const reportEnd = new Date(dateTo);
    if (isNaN(checkDate.getTime())) return '-';
    if (checkDate > reportEnd) return formatDate(dateTo);
    return formatDate(dend);
  };

  // Получаем количество ссылок до dateFrom
  useEffect(() => {
    const fetchLinksBefore = async () => {
      try {
        if (!dateFrom) return setLinksBefore(0);
        const params = new URLSearchParams();
        params.append('site_id', siteId);
        params.append('aurl', site?.dns || '');
        params.append('limit', '1');
        params.append('dat_to', new Date(new Date(dateFrom).getTime() - 24*60*60*1000).toISOString().split('T')[0]);
        const response = await fetch(`${API_URL}${currentLang}/links?${params.toString()}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        });
        const data = await response.json();
        if (response.ok && data.success) {
          setLinksBefore(data.pagination?.total || 0);
        } else {
          setLinksBefore(0);
        }
      } catch {
        setLinksBefore(0);
      }
    };
    if (siteId && site?.dns && dateFrom) fetchLinksBefore();
  }, [siteId, site, dateFrom, currentLang, token, API_URL]);

  // Получаем ссылки за период отчета
  useEffect(() => {
    const fetchLinksPeriod = async () => {
      try {
        setLoading(true);
        setError('');
        const params = new URLSearchParams();
        if (dateFrom) params.append('dat_from', dateFrom);
        if (dateTo) params.append('dat_to', dateTo);
        params.append('site_id', siteId);
        params.append('aurl', site?.dns || '');
        params.append('limit', '1000');
        const response = await fetch(`${API_URL}${currentLang}/links?${params.toString()}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        });
        const data = await response.json();
        if (response.ok && data.success) {
          setLinksPeriod(data.links || []);
        } else {
          throw new Error(data.message || 'Ошибка загрузки ссылок');
        }
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };
    if (siteId && site?.dns) fetchLinksPeriod();
  }, [siteId, site, dateFrom, dateTo, currentLang, token, API_URL]);

  // Формируем данные для накопительного графика
  useEffect(() => {
    if (!linksPeriod.length && !linksBefore) {
      setChartData({ series: [], options: {} });
      return;
    }
    // Группируем по дате размещения только за период
    const dateMap = {};
    linksPeriod.forEach(link => {
      const d = link.dat ? link.dat.split('T')[0] : null;
      if (d) {
        dateMap[d] = (dateMap[d] || 0) + 1;
      }
    });
    // Сортируем даты
    const sortedDates = Object.keys(dateMap).sort();
    // Кумулятивный массив
    let cumulative = linksBefore;
    const chartDates = [];
    const seriesData = [];
    // Первая точка - на дату начала отчета
    if (dateFrom) {
      chartDates.push(new Date(dateFrom).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' }));
      seriesData.push(linksBefore);
    }
    sortedDates.forEach(date => {
      cumulative += dateMap[date];
      chartDates.push(new Date(date).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' }));
      seriesData.push(cumulative);
    });
    setChartData({
      series: [{
        name: 'Ссылочная масса',
        data: seriesData
      }],
      options: {
        chart: {
          type: 'line',
          toolbar: { show: false }
        },
        stroke: {
          curve: 'smooth',
          width: 3
        },
        xaxis: {
          categories: chartDates,
          labels: {
            style: {
              colors: '#A3AED0',
              fontSize: '10px',
              fontWeight: '500',
            }
          }
        },
        yaxis: {
          show: true,
          labels: {
            style: {
              colors: '#A3AED0',
              fontSize: '10px',
              fontWeight: '500',
            }
          }
        },
        colors: ['#3B82F6'],
        fill: {
          type: 'solid',
          opacity: 0.2
        },
        grid: {
          show: true,
          borderColor: '#E2E8F0',
          strokeDashArray: 3
        },
        tooltip: {
          y: {
            formatter: val => `${val} ссылок`
          }
        }
      }
    });
  }, [linksPeriod, linksBefore, dateFrom]);

  // Сводная информация
  const totalLinks = linksBefore + linksPeriod.length;
  const linksInPeriod = linksPeriod.length;

  useEffect(() => {
    if (onStats) {
      onStats({ linksInPeriod: linksPeriod.length });
    }
  }, [linksPeriod, onStats]);

  if (!site?.dns) return null;

  return (
    <div className="bg-white p-8">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">
          Линкбилдинг / внешняя оптимизация
        </h2>
        <p className="text-gray-600 mb-4">
          Список размещённых ссылок на сайт за период отчёта
        </p>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 w-full">
          <div className="text-lg font-semibold text-blue-700 w-full">
            Всего размещено ссылок на сайт: {totalLinks}
          </div>
          <div className="text-md text-gray-700">
            За период отчёта размещено: <span className="font-bold text-green-700">{linksInPeriod}</span>
          </div>
        </div>
        {chartData.series.length > 0 && (
          <div className="bg-white rounded-lg border p-4 mb-8">
            <h3 className="text-md font-semibold mb-2 text-center">Рост ссылочной массы</h3>
            <div className="h-64">
              <LineChart series={chartData.series} options={chartData.options} />
            </div>
          </div>
        )}
      </div>
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="flex items-center space-x-2">
            <span className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-500"></span>
            <span className="text-gray-600">Загрузка ссылок...</span>
          </div>
        </div>
      ) : error ? (
        <div className="flex justify-center items-center h-64">
          <div className="text-center text-red-500">{error}</div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full border border-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="border px-3 py-2 text-xs font-medium text-gray-700">Дата размещения</th>
                <th className="border px-3 py-2 text-xs font-medium text-gray-700">URL донора</th>
                <th className="border px-3 py-2 text-xs font-medium text-gray-700">URL акцептора</th>
                <th className="border px-3 py-2 text-xs font-medium text-gray-700">Анкор</th>
                <th className="border px-3 py-2 text-xs font-medium text-gray-700">Язык</th>
                <th className="border px-3 py-2 text-xs font-medium text-gray-700">SEO параметры</th>
                <th className="border px-3 py-2 text-xs font-medium text-gray-700">Последняя проверка</th>
              </tr>
            </thead>
            <tbody>
              {linksPeriod.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-8 text-gray-500">Ссылки не найдены</td>
                </tr>
              ) : (
                linksPeriod.map((link) => (
                  <tr key={link.id} className="hover:bg-gray-50">
                    <td className="border px-3 py-2 text-xs text-gray-900">{formatDate(link.dat)}</td>
                    <td className="border px-3 py-2 text-xs text-blue-700 max-w-[220px] truncate">
                      <a href={link.durl} target="_blank" rel="noopener noreferrer" className="hover:underline">{link.durl}</a>
                    </td>
                    <td className="border px-3 py-2 text-xs text-blue-700 max-w-[220px] truncate">
                      <a href={link.aurl} target="_blank" rel="noopener noreferrer" className="hover:underline">{link.aurl}</a>
                    </td>
                    <td className="border px-3 py-2 text-xs text-gray-900">{link.text}</td>
                    <td className="border px-3 py-2 text-xs">
                      <span className={`px-2 py-1 text-xs font-bold rounded-full ${
                        link.lang === 'ru' ? 'bg-blue-100 text-blue-800' :
                        link.lang === 'en' ? 'bg-green-100 text-green-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {link.lang?.toUpperCase() || '-'}
                      </span>
                    </td>
                    <td className="border px-3 py-2 text-xs">
                      {link.stat_pos && link.stat_pos !== 'unknown' && (
                        <span className="px-1 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded mr-1">Позиция: {link.stat_pos}</span>
                      )}
                      {link.stat_attr && link.stat_attr !== '' && (
                        <span className="px-1 py-0.5 text-xs font-medium bg-purple-100 text-purple-800 rounded">{link.stat_attr}</span>
                      )}
                    </td>
                    <td className="border px-3 py-2 text-xs text-gray-900">{getLastCheckDate(link.dend)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Linkbuilding; 